import { gql, useQuery } from '@apollo/client';
import type { Georole } from '@domain/person';

const query = gql`
  query RoleNames {
    roleNames
  }
`;

interface RoleNamesQuery {
  roleNames: string[];
}

interface UseRoleNamesReturns {
  loading: boolean;
  roleNames?: string[];
}

export default function useRoleNames(): UseRoleNamesReturns {
  const { error, loading, data } = useQuery<RoleNamesQuery>(query);
  if (error != null) throw error;
  if (loading) {
    return { loading: true };
  } else {
    if (data == null)
      throw new Error('useRoleNames: loading finished but data is undefined');
    return { loading: false, roleNames: data.roleNames };
  }
}

export const georolesQuery = gql`
  query ($idnumber: String!) {
    person(idnumber: $idnumber) {
      darn
      georoles {
        roleName
        structures {
          type
          code
          name
        }
      }
    }
  }
`;

interface PersonGeorolesQuery {
  person: {
    darn: number;
    georoles: Georole[];
  };
}

interface UsePersonGeorolesReturns {
  georoles?: Georole[];
  loading: boolean;
  refetch: () => Promise<void>;
}

interface PersonQueryVars {
  idnumber: string;
}

export function usePersonGeoroles(idnumber: string): UsePersonGeorolesReturns {
  const { error, loading, data, refetch } = useQuery<
    PersonGeorolesQuery,
    PersonQueryVars
  >(georolesQuery, {
    variables: { idnumber },
    fetchPolicy: 'cache-and-network',
  });
  if (error != null) throw error;
  return {
    georoles: data?.person.georoles,
    loading,
    refetch: async () => {
      await refetch();
    },
  };
}
